// TARJETA DEL RADAR OPERATIVO — UN solo componente adaptable (5 variantes de densidad).
// Sólo observa: no hay Tomar, Cerrar, llamar, escalar ni notas. Lo único operativo es «Ir a
// Central», que abre el caso QUE YA EXISTE con el deep-link vigente.
// Texto de estado SEGURO: sin eventos recientes NO se afirma «todo bien» (§13).
import { ArrowUpRight, ShieldAlert, Activity, Clock, MapPin, Star } from "lucide-react";
import { acentoPersona, verboEvento } from "@/lib/consolaV3/meta";
import { coberturaPeorLabel } from "@/lib/persona/contract";
import { HECHOS_VISIBLES } from "@/lib/radar/densidad";
import { fmtHoraCorta, esHoyTijuana } from "@/lib/format";

// GC92 · toda hora de la tarjeta se rinde en hora local de TIJUANA (operación), nunca en el huso
// del dispositivo que consulta.
const horaCorta = fmtHoraCorta;
// Tope duro de contexto: 3 hechos DE HOY. Los anteriores no se presentan como novedad actual.
const MAX_CONTEXTO_HOY = 3;

const TAM = {
  xl: { pad: "p-4", nombre: "text-lg", meta: "text-xs", verbo: "text-sm", hecho: "text-xs" },
  l: { pad: "p-3.5", nombre: "text-base", meta: "text-[11px]", verbo: "text-[13px]", hecho: "text-[11px]" },
  m: { pad: "p-3", nombre: "text-sm", meta: "text-[11px]", verbo: "text-[12px]", hecho: "text-[11px]" },
  s: { pad: "p-2.5", nombre: "text-[13px]", meta: "text-[10px]", verbo: "text-[11px]", hecho: "text-[10px]" },
  xs: { pad: "p-2", nombre: "text-[12px]", meta: "text-[10px]", verbo: "text-[10px]", hecho: "text-[10px]" },
};

export default function RadarCard({ t, variant = "m", nowMs, ventanaMin = 30, ubicacion, onFicha, onCentral,
  favorito = false, editandoVista = false, onFavorito }) {
  const z = TAM[variant] || TAM.m;
  const caso = t.caso;
  const ac = caso ? acentoPersona(caso.prioridad) : null;
  // GC106 · el acento sale de la PRIORIDAD REAL del caso que el backend ya eligió (incluida su
  // precedencia cuando hay varios). La UI sólo la rinde: no reordena ni reinterpreta.
  const pk = caso ? (caso.prioridad === "maxima" ? "alta" : caso.prioridad || "unknown") : null;
  const cobRevisar = ["perdida", "no_confirmable", "degradada"].includes(t.cobertura_peor);
  const maxHechos = Math.min(HECHOS_VISIBLES[variant] || 1, MAX_CONTEXTO_HOY);
  // GC92 · CONTEXTO = hechos de HOY (día local de Tijuana) MÁS los que siguen dentro de la ventana
  // de actividad aunque sean de ayer (cruce de medianoche): ésos se rotulan «Ayer HH:mm» y NO se
  // convierten en hechos de hoy. El caso abierto se muestra siempre, aunque sea de ayer; el resto
  // de hechos anteriores se anuncia y queda en la ficha y en la Bitácora.
  const hechosTodos = t.hechos || [];
  const esHoy = (h) => (h.hoy ?? esHoyTijuana(h.hora));
  const esReciente = (h) => h.reciente ?? (!!h.hora && nowMs - new Date(h.hora).getTime() <= ventanaMin * 60000);
  const hechos = hechosTodos.filter((h) => esHoy(h) || esReciente(h)).slice(0, maxHechos);
  const anteriores = hechosTodos.filter((h) => !esHoy(h) && !esReciente(h)).length;
  // Institucional: la ubicación estructural (habitación/cama) manda y llega ya resuelta.
  const ubic = ubicacion || [t.sitio_nombre, t.zona_nombre || t.cama_etiqueta].filter(Boolean).join(" · ")
    || t.ciudad || "—";
  const compacto = variant === "s" || variant === "xs";
  const abrir = () => (caso ? onCentral(t) : onFicha(t));

  return (
    <div data-testid={`radar-card-${t.id}`} data-categoria={t.categoria} role="button" tabIndex={0}
      data-priority={pk || undefined} data-caso={caso ? "si" : "no"}
      data-actividad={!caso && t.actividad_reciente ? "si" : "no"}
      onClick={abrir} onKeyDown={(e) => { if (e.key === "Enter") abrir(); }}
      aria-label={caso
        ? `${t.nombre} · ${caso.prioridad_label} · ${caso.en_atencion ? "en atención" : "pendiente"} · abrir el caso en Central`
        : `${t.nombre} · sin caso activo · abrir la ficha`}
      className={`radar-card flex cursor-pointer flex-col gap-1.5 ${z.pad} text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]`}>

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={`truncate font-extrabold tracking-tight text-slate-900 ${z.nombre}`}>
            {t.nombre}{t.edad && (variant === "xl" || variant === "l") ? ` · ${t.edad}` : ""}
          </p>
          <p className={`flex items-center gap-1 truncate text-slate-500 ${z.meta}`}>
            <MapPin className="h-3 w-3 shrink-0" />{compacto && !ubicacion ? (t.sitio_nombre || t.ciudad || "—") : ubic}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {(editandoVista || favorito) && onFavorito && (
            <button type="button" data-testid={`radar-favorito-${t.id}`}
              onClick={(e) => { e.stopPropagation(); onFavorito(t); }}
              aria-pressed={favorito}
              title={favorito ? "Quitar de mi vista" : "Añadir a mi vista"}
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors ${favorito ? "text-[#E8A317]" : "text-slate-300 hover:text-[#E8A317]"}`}>
              <Star className="h-4 w-4" fill={favorito ? "currentColor" : "none"} />
            </button>
          )}
          {cobRevisar && (
            <span data-testid={`radar-cobertura-${t.id}`} title={coberturaPeorLabel(t.cobertura_peor)}
              className="shrink-0 rounded bg-[#9B2C2C]/10 p-1 text-[#9B2C2C]"><ShieldAlert className="h-3.5 w-3.5" /></span>
          )}
        </div>
      </div>

      {caso ? (
        <div className="radar-card-estado px-2 py-1.5">
          <p className={`flex items-center gap-1.5 font-extrabold uppercase leading-tight ${z.verbo} ${ac.text}`}>
            <Activity className="h-3.5 w-3.5 shrink-0" />{verboEvento(caso.tipo)}
          </p>
          <p className={`mt-0.5 flex flex-wrap items-center gap-x-1.5 font-bold ${z.meta} ${ac.text}`}>
            <span>{(caso.prioridad_label || caso.prioridad || "").toUpperCase()}</span>
            <span>· {caso.en_atencion ? "En atención" : "Pendiente"}</span>
            {caso.desde && <span className="font-mono tabular-nums">· {horaCorta(caso.desde)}</span>}
          </p>
          {!compacto && caso.en_atencion && caso.operador && (
            <p className={`text-slate-500 ${z.meta}`}>{caso.operador}</p>
          )}
          {t.casos_activos > 1 && (
            <p data-testid={`radar-multicaso-${t.id}`} className={`mt-0.5 font-bold ${z.meta} ${ac.text}`}>
              +{t.casos_activos - 1} {t.casos_activos - 1 === 1 ? "caso activo más" : "casos activos más"}
            </p>
          )}
        </div>
      ) : t.actividad_reciente ? (
        <p data-testid={`radar-actividad-${t.id}`} className={`flex items-center gap-1.5 font-semibold text-[#0284C7] ${z.verbo}`}>
          <Clock className="h-3.5 w-3.5 shrink-0" /> Actividad reciente
        </p>
      ) : (
        // R5 · presentación NEUTRAL y compacta: la frase repetida en todas las tarjetas se
        // sustituye por una marca discreta. NO se afirma que la persona esté bien: la ausencia de
        // actividad observada no es ausencia de riesgo (el title lo dice explícitamente).
        <p data-testid={`radar-sin-actividad-${t.id}`}
          title={`Sin hechos observados en los últimos ${ventanaMin} minutos. No confirma que la persona esté bien ni que la cobertura esté activa.`}
          className={`flex items-center gap-1 text-slate-400 ${z.hecho}`}>
          <span aria-hidden="true" className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
          {compacto ? <span className="sr-only">Sin hechos observados en la ventana reciente</span>
            : <span>Sin hechos observados ({ventanaMin} min)</span>}
        </p>
      )}

      {/* VIS-01 · la cobertura y su evidencia dejan de competir: el chip queda JUNTO al bloque de
          estado (misma señal, lectura contigua) en vez de al final, después del contexto
          reciente. No se elimina ni reinterpreta ninguna de las dos señales. */}
      {cobRevisar && !compacto && (
        <p className={`w-fit rounded bg-[#9B2C2C]/10 px-1.5 py-0.5 font-medium text-[#9B2C2C] ${z.hecho}`}>
          {coberturaPeorLabel(t.cobertura_peor)}
        </p>
      )}

      {hechos.length > 0 && (
        <div className="flex flex-col gap-0.5">
          {!compacto && variant === "xl" && <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Contexto de hoy</p>}
          {hechos.map((h, i) => (
            <p key={i} data-testid={`radar-hecho-${t.id}-${i}`} data-hoy={esHoy(h) ? "true" : "false"}
              className={`truncate ${z.hecho} ${esReciente(h) ? "text-slate-600" : "text-slate-500"}`}>
              <span className="font-mono tabular-nums">{esHoy(h) ? "" : "Ayer "}{horaCorta(h.hora)}</span> · {verboEvento(h.tipo)}
            </p>
          ))}
        </div>
      )}

      {anteriores > 0 && !compacto && (
        <p data-testid={`radar-hechos-anteriores-${t.id}`}
          title="Hechos de días anteriores. No son actividad de hoy: quedan disponibles en la ficha y en la Bitácora."
          className={`truncate text-slate-400 ${z.hecho}`}>
          {anteriores === 1 ? "1 hecho anterior · en ficha y Bitácora" : `${anteriores} hechos anteriores · en ficha y Bitácora`}
        </p>
      )}

      {caso && (
        <button data-testid={`radar-ir-central-${t.id}`} type="button"
          onClick={(e) => { e.stopPropagation(); onCentral(t); }}
          className={`mt-auto inline-flex min-h-9 w-full items-center justify-center gap-1 rounded-lg bg-[#012472] px-2.5 font-bold text-white transition-colors hover:bg-[#012472]/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3] ${z.hecho}`}>
          Ir a Central <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
